import * as Schema from "effect/Schema";
import type {
  GuestActionGrant,
  GuestActionRequest,
  GuestProvisioningGrant,
  GuestProvisioningRequest,
} from "./guestEnvironment.ts";

/**
 * AFAR Networks and Organizations as AFAR Connect reports them (ADR-0146, ADR-0148). A machine is
 * network metadata only: nothing about the work running on it.
 */

export const OrganizationRole = Schema.Literals(["owner", "member"]);
export type OrganizationRole = typeof OrganizationRole.Type;

export const MemberEnvironmentLimits = Schema.Struct({
  cpuCount: Schema.Int,
  memoryBytes: Schema.Number,
  diskBytes: Schema.Number,
});
export type MemberEnvironmentLimits = typeof MemberEnvironmentLimits.Type;

export const AfarNetworkMachine = Schema.Struct({
  guestId: Schema.optionalKey(Schema.String),
  parentMachineId: Schema.optionalKey(Schema.String),
  accountId: Schema.optionalKey(Schema.String),
  id: Schema.String,
  name: Schema.String,
  addresses: Schema.NullOr(Schema.Array(Schema.String)),
  os: Schema.String,
  arch: Schema.String,
  tags: Schema.NullOr(Schema.Array(Schema.String)),
  online: Schema.Boolean,
  lastSeen: Schema.optionalKey(Schema.String),
  createdAt: Schema.optionalKey(Schema.String),
  vmCapable: Schema.Boolean,
  memberEnvironmentLimits: Schema.NullOr(MemberEnvironmentLimits),
  /** Whether its owner lets it be used; off, Connect introduces no client to it. */
  enabled: Schema.optionalKey(Schema.Boolean),
});
export type AfarNetworkMachine = typeof AfarNetworkMachine.Type;

export const AfarNetworkSummary = Schema.Struct({
  id: Schema.String,
  owner: Schema.Struct({ kind: Schema.Literals(["account", "organization"]) }),
  organization: Schema.NullOr(
    Schema.Struct({ id: Schema.String, name: Schema.String, role: OrganizationRole }),
  ),
  permissions: Schema.Struct({ use: Schema.Boolean, manage: Schema.Boolean }),
  machines: Schema.Array(AfarNetworkMachine),
});
export type AfarNetworkSummary = typeof AfarNetworkSummary.Type;

export const AfarNetworkList = Schema.Struct({ networks: Schema.Array(AfarNetworkSummary) });
export type AfarNetworkList = typeof AfarNetworkList.Type;

export const NetworkEnrollmentCode = Schema.Struct({
  code: Schema.String,
  controlUrl: Schema.String,
  connectOrigin: Schema.String,
  expiresAt: Schema.Number,
  /** An Organization's machine hosts Member Environments rather than running one (ADR-0148). */
  organizationHost: Schema.Boolean,
  command: Schema.String,
});
export type NetworkEnrollmentCode = typeof NetworkEnrollmentCode.Type;

/**
 * The command that puts a machine on a network with an Enrollment Code. A personal machine joins
 * its own Environment Service; an Organization's installs the host for Member Environments, which
 * needs an administrator (ADR-0148): `sudo` on macOS and Linux, where it names the binary by path
 * because `sudo` does not search the user's `PATH`, and an administrator PowerShell on Windows.
 */
export const networkEnrollmentCommand = (
  enrollment: Pick<
    NetworkEnrollmentCode,
    "organizationHost" | "controlUrl" | "connectOrigin" | "code"
  >,
  platform: "posix" | "windows",
): string =>
  [
    enrollment.organizationHost
      ? platform === "windows"
        ? "afar-research network host install"
        : 'sudo "$(command -v afar-research)" network host install'
      : "afar-research network join",
    `--control-url ${enrollment.controlUrl}`,
    `--connect-origin ${enrollment.connectOrigin}`,
    `--code ${enrollment.code}`,
  ].join(" ");

/**
 * What `/v1/networks/connect` hands back: a route through the Network Gateway (ADR-0147) and a
 * grant the Environment minted for this client. The endpoint carries a short-lived route ticket in
 * its path, so every reconnect asks Connect again, which is when membership is re-checked.
 */
export const NetworkBrokeredConnection = Schema.Struct({
  networkId: Schema.String,
  machineId: Schema.String,
  environmentId: Schema.String,
  label: Schema.String,
  endpoint: Schema.String,
  endpointExpiresAt: Schema.Number,
  credential: Schema.String,
  expiresAt: Schema.Number,
});
export type NetworkBrokeredConnection = typeof NetworkBrokeredConnection.Type;

export const OrganizationMember = Schema.Struct({
  accountId: Schema.String,
  role: OrganizationRole,
  name: Schema.NullOr(Schema.String),
  email: Schema.NullOr(Schema.String),
  joinedAt: Schema.Number,
});
export type OrganizationMember = typeof OrganizationMember.Type;

export const OrganizationInvitation = Schema.Struct({
  id: Schema.String,
  email: Schema.String,
  role: OrganizationRole,
  createdAt: Schema.Number,
  expiresAt: Schema.Number,
});
export type OrganizationInvitation = typeof OrganizationInvitation.Type;

export const OrganizationView = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  /** The reading account's Connect id, as `members` names it, and its role. */
  accountId: Schema.String,
  role: OrganizationRole,
  members: Schema.Array(OrganizationMember),
  invitations: Schema.Array(OrganizationInvitation),
});
export type OrganizationView = typeof OrganizationView.Type;

export const CreatedOrganization = Schema.Struct({
  organizationId: Schema.String,
  networkId: Schema.String,
});
export type CreatedOrganization = typeof CreatedOrganization.Type;

/** `acceptUrl` is present only when Connect has no mail service and the Owner shares the link. */
export const SentOrganizationInvitation = Schema.Struct({
  invitationId: Schema.String,
  delivered: Schema.Boolean,
  acceptUrl: Schema.NullOr(Schema.String),
});
export type SentOrganizationInvitation = typeof SentOrganizationInvitation.Type;

/** The operations the Runtime Web Client offers; Desktop exposes none of them (ADR-0146). */
export const NetworkRemovalImpact = Schema.Struct({
  guests: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
});
export type NetworkRemovalImpact = typeof NetworkRemovalImpact.Type;

/** A machine Connect sets up over SSH with a password typed once; `port` defaults to SSH's. */
export interface SshMachineSetup {
  readonly host: string;
  readonly port?: number;
  readonly username: string;
  readonly password: string;
}

export const SshMachineSetupStarted = Schema.Struct({ expiresAt: Schema.Number });
export type SshMachineSetupStarted = typeof SshMachineSetupStarted.Type;

export interface AfarNetworkBridge {
  networks(): Promise<AfarNetworkList>;
  createEnrollmentCode(networkId: string): Promise<NetworkEnrollmentCode>;
  guestProvisioning?(
    networkId: string,
    parentMachineId: string,
    request: GuestProvisioningRequest,
  ): Promise<GuestProvisioningGrant>;
  guestAction?(
    networkId: string,
    parentMachineId: string,
    request: GuestActionRequest,
  ): Promise<GuestActionGrant>;
  networkRemovalImpact?(networkId: string, machineId: string): Promise<NetworkRemovalImpact>;
  removeNetworkMachine(networkId: string, machineId: string): Promise<void>;
  /** Lets a machine be used, or not: off, nobody connects to it until it is switched on again. */
  setNetworkMachineEnabled(networkId: string, machineId: string, enabled: boolean): Promise<void>;
  /**
   * Has Connect set a machine up over SSH and join it to the network; resolves once the setup has
   * started on the machine, which then joins on its own within a few minutes.
   */
  setUpMachineOverSsh(networkId: string, setup: SshMachineSetup): Promise<SshMachineSetupStarted>;
  setMemberEnvironmentLimits(
    networkId: string,
    machineId: string,
    limits: MemberEnvironmentLimits,
  ): Promise<void>;
  /** An Owner stops or deletes one member's Member Environment on a host, without entering it. */
  memberEnvironmentAction(
    networkId: string,
    machineId: string,
    accountId: string,
    action: "stop" | "delete",
  ): Promise<void>;
  /** Introduces this client to a machine and remembers it for reconnects. */
  connectNetworkMachine(request: { networkId: string; machineId: string }): Promise<
    | {
        readonly _tag: "NetworkMachineConnected";
        readonly environmentId: string;
        readonly label: string;
      }
    | { readonly _tag: "NetworkMachineRefused"; readonly code: string; readonly status: number }
  >;
  /**
   * The machines this device reached through a network, which it keeps as Environments the way it
   * keeps the Cloud Workspaces it added. Read synchronously from this device's own storage.
   */
  rememberedMachines(): ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly networkId: string;
    readonly machineId: string;
  }>;
  /** Stops reconnecting to one; the machine and its membership are untouched. */
  forgetNetworkMachine(environmentId: string): void;
  createOrganization(name: string): Promise<CreatedOrganization>;
  organization(organizationId: string): Promise<OrganizationView>;
  inviteToOrganization(
    organizationId: string,
    email: string,
    role: OrganizationRole,
  ): Promise<SentOrganizationInvitation>;
  revokeOrganizationInvitation(organizationId: string, invitationId: string): Promise<void>;
  acceptOrganizationInvitation(token: string): Promise<{ organizationId: string }>;
  changeOrganizationRole(
    organizationId: string,
    accountId: string,
    role: OrganizationRole,
  ): Promise<void>;
  removeOrganizationMember(organizationId: string, accountId: string): Promise<void>;
  deleteOrganization(organizationId: string): Promise<void>;
}

/**
 * What one Environment in a Machine Pool asks another (ADR-0149), over the delegated session that
 * destination issued. Paths are under `MACHINE_POOL_DELEGATION_PATH_PREFIX`.
 */
export const MachinePoolThreadRequest = Schema.Struct({
  projectId: Schema.String,
  prompt: Schema.String,
  title: Schema.optional(Schema.String),
});
export type MachinePoolThreadRequest = typeof MachinePoolThreadRequest.Type;

export const MachinePoolResources = Schema.Struct({
  environmentId: Schema.String,
  label: Schema.String,
  resources: Schema.NullOr(
    Schema.Struct({
      sampledAt: Schema.Number,
      cpuUtilization: Schema.NullOr(Schema.Number),
      cpuCount: Schema.Number,
      availableMemoryBytes: Schema.Number,
      totalMemoryBytes: Schema.Number,
    }),
  ),
});
export type MachinePoolResources = typeof MachinePoolResources.Type;

/** One Environment in a project's Machine Pool, as clients see it: never its credential. */
export const MachinePoolEntryView = Schema.Struct({
  ownerProjectId: Schema.String,
  environmentId: Schema.String,
  projectId: Schema.String,
  label: Schema.String,
  address: Schema.String,
  addedAt: Schema.Number,
});
export type MachinePoolEntryView = typeof MachinePoolEntryView.Type;

export class MachinePoolError extends Schema.TaggedErrorClass<MachinePoolError>()(
  "MachinePoolError",
  {
    reason: Schema.Literals([
      "not-on-network",
      "own-environment",
      "already-in-pool",
      "not-in-pool",
      "unreachable",
      "refused",
      "wrong-environment",
      "storage",
    ]),
    message: Schema.String,
  },
) {}

export const MachinePoolAddInput = Schema.Struct({
  projectId: Schema.String,
  environmentId: Schema.String,
  remoteProjectId: Schema.String,
  label: Schema.String,
  /** The destination's address on the AFAR Network. */
  address: Schema.String,
  /** A delegate grant the destination issued, redeemed here against it. */
  grant: Schema.String,
});
export type MachinePoolAddInput = typeof MachinePoolAddInput.Type;
